import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IDynamicFormValue,
  IMultiSelectControlField2,
  ISelectOption,
  toFormFields2
} from 'src/lib';
import { getNextValues } from 'src/components/filters';
import DRAWER_VIEW_TYPE from '../types/drawer-view-type';
import {
  getGridData,
  submitOpportunitiesForProduct,
  submitOpportunitiesForProductsInBulk,
  addBulkCommentsByOpportunity,
  resetAllOpportunities,
  getProductsWithComments,
  getOpportunitySummaryTableData,
  addNewComment,
  deleteCommentById,
  updateComment,
  getAddSKUFormFields,
  getLocalFiltersData,
  getFilterFieldsForCogs,
  getBulkInputsTableOpportunity,
  getBulkInputsTableRows,
  getPlannerToggles,
  getDynamicDrawerData,
  getEanZrepTableData,
  getOpportunitySummaryTableDataV2,
  resetDrawerDetails,
  getTableDataForCogs,
  resetCogsSimulationData,
  getPHRecommendations
} from './planner-slice-async-thunks';
import {
  DrawerProductItem,
  IOpportunity,
  IProductComment
} from '../types/ppg-sku-drawer.model';
import { isOpportunitySelectionChanged } from '../utils/opportunity-utils';
import { setRowItemOpportunity } from '../utils/bulk-input-utils';
import { IPlannerState } from '../types/planner-state.model';

import { IBulkCommentsDrawerResponse } from '../types/bulk-comment-drawer';

const initialState: IPlannerState = {
  fetchingMetaData: false,
  fetchingLocalFiltersData: false,
  fetchingCogsFiltersData: false,
  fetchingCogsTableData: false,
  isSimulationFailed: false,
  cogsTableData: {},
  baseCogsTableData: {},
  isClearClicked: false,
  isSimulating: false,
  localFilterFormFields: [],
  localFilterValue: {},
  localFilterInitialValues: {},
  isLocalFiltersFetched: true,
  fetchingGridData: false,
  enableReset: true,
  cogsDialogVisible: false,
  enableSimulation: true,
  plannerMetaData: {},
  isFetchingToggleData: false,
  toggles: [],
  summaryOpportunitiesTableData: {
    legend: { metadata: [] },
    ownKPIColumnMetadata: {
      inputCols: [],
      resultCols: []
    },
    retailerKPIColumnMetadata: {
      inputCols: [],
      resultCols: []
    },
    metadata: [],
    show_edited: {
      enabled: false,
      label: ''
    },
    row: [],
    toggle: {
      metadata: [],
      data: {}
    },
    output_toggle: {
      metadata: [],
      data: {}
    },
    footer: {
      metadata: {
        enabled: false,
        label: ''
      },
      row: []
    },
    header: []
  },
  bulkInputsTableOpportunities: {
    ppg: [],
    sku: []
  },
  toggledView: '',
  gridLayout: {
    column_headers: [],
    grid_data: [],
    row_headers: []
  },
  gridCellMetadata: [],
  isSummaryRefreshRequired: false,
  bulkInputsMetadata: {
    rows: [],
    originalRows: [],
    opportunityTabs: [],
    isAddingComment: false,
    isApplyingOpportunities: false,
    isOpportunitySelectionChanged: false
  },
  productsWithComments: [],
  drawerViewMetadata: {
    drawerData: {
      opportunities: {},
      title: {
        parent_title: '',
        child_title: ''
      },
      level_mapping: {},
      drawer_data: [
        {
          parent: {
            data: {
              title_key: '',
              selected_opportunity: [],
              imgUrl: '',
              detail_metrics: [],
              key: '',
              metrics: [],
              comment_details: [],
              id: -1
            }
          },
          children: []
        }
      ]
    },
    isDrawerOpen: false,
    viewType: null,
    productId: null,
    isLoading: false,
    opportunitySelectionMap: [],
    initialChildOpportunities: [],
    initialParentOpportunities: [],
    isSubmittingDrawerData: false,
    productKeyRef: ''
  },
  eanZrepTableMetadata: { isLoading: false, productMapping: {} },
  isCommentLoading: false,
  isFetchingOpportunitySummaryData: false,
  isFetchingPhRecommendations: false,
  opportunitySummaryDetails: [],
  formFields: [],
  addSKUFieldCombinations: [],
  isFetchingFormFields: false,
  isFetchingBulkInputs: false,
  appliedFilters: [],
  filtersForCogs: [],
  topCogsFiltersValue: {
    filters: [],
    combinations: [],
    initialValues: {}
  },
  topCogsFiltersDefaultValue: {
    filters: [],
    combinations: [],
    initialValues: {}
  },
  isSKUEditedClick: false,
  isViewPhRecommendation: false
};

/* eslint-disable no-param-reassign */
const plannerSliceV2 = createSlice({
  name: 'plannerV2',
  initialState,
  reducers: {
    // reducer for toggling cogs dialog visibility
    setCogsDialogVisible(state) {
      state.cogsDialogVisible = !state.cogsDialogVisible;
    },
    resetCogsSimulation(state) {
      state.enableSimulation = false;
    },
    handleCogsSimulationFlagValue(state) {
      state.isSimulating = true;
    },
    handleCogsFailedFlagValue(state) {
      state.isSimulationFailed = false;
    },
    resetBulkInputsTableRows(state) {
      state.bulkInputsMetadata.rows = [];
      state.bulkInputsMetadata.isOpportunitySelectionChanged = false;
    },
    setOpportunities(
      state,
      action: PayloadAction<{
        parentIndex: number;
        childIndex: number;
        newOpportunities: number[];
        changedOpportunity: IOpportunity;
      }>
    ) {
      setRowItemOpportunity(state, action.payload);
      /**
       * Computing if the selection has changed
       */
      state.bulkInputsMetadata.isOpportunitySelectionChanged =
        state.bulkInputsMetadata.rows.some((row, index) =>
          isOpportunitySelectionChanged(
            state.bulkInputsMetadata.originalRows[index].opportunities,
            state.bulkInputsMetadata.originalRows[index].subRows.map(
              (subRow) => subRow.opportunities
            ),
            row.opportunities,
            row.subRows.map((subRow) => subRow.opportunities)
          )
        );
    },
    setAllOpportunities(
      state,
      action: PayloadAction<{
        changedOpportunity: IOpportunity;
        isOpportunityRemoved: boolean;
      }>
    ) {
      const { changedOpportunity, isOpportunityRemoved } = action.payload;
      state.bulkInputsMetadata.rows.forEach((row) => {
        const newOpportunitiesForParentItem = row.opportunities
          .filter((opportunity) => {
            if (isOpportunityRemoved) {
              return opportunity !== changedOpportunity.pk_id;
            }
            // If the opportunity is added and it is supposed to disable any of the already checked opportunity
            // then we remove the checked opportunity
            return !changedOpportunity.disables_opportunity.includes(
              opportunity
            );
          })
          .slice();
        if (
          !isOpportunityRemoved &&
          !newOpportunitiesForParentItem.includes(changedOpportunity.pk_id)
        ) {
          newOpportunitiesForParentItem.push(changedOpportunity.pk_id);
        }
        setRowItemOpportunity(state, {
          changedOpportunity,
          childIndex: null,
          newOpportunities: newOpportunitiesForParentItem,
          parentIndex: row.itemIndex
        });
        row.subRows.forEach((subRow) => {
          const newOpportunitiesForChildItem = subRow.opportunities
            .filter((opportunity) => {
              if (isOpportunityRemoved) {
                return opportunity !== changedOpportunity.pk_id;
              }
              // If the opportunity is added and it is supposed to disable any of the already checked opportunity
              // then we remove the checked opportunity
              return !changedOpportunity.disables_opportunity.includes(
                opportunity
              );
            })
            .slice();
          if (!isOpportunityRemoved) {
            if (
              !newOpportunitiesForChildItem.includes(changedOpportunity.pk_id)
            ) {
              newOpportunitiesForChildItem.push(changedOpportunity.pk_id);
            }
          }
          setRowItemOpportunity(state, {
            changedOpportunity,
            childIndex: subRow.itemIndex,
            newOpportunities: newOpportunitiesForChildItem,
            parentIndex: row.itemIndex
          });
        });
      });
      /**
       * Computing if the selection has changed
       */
      state.bulkInputsMetadata.isOpportunitySelectionChanged =
        state.bulkInputsMetadata.rows.some((row, index) =>
          isOpportunitySelectionChanged(
            state.bulkInputsMetadata.originalRows[index].opportunities,
            state.bulkInputsMetadata.originalRows[index].subRows.map(
              (subRow) => subRow.opportunities
            ),
            row.opportunities,
            row.subRows.map((subRow) => subRow.opportunities)
          )
        );
    },
    togglePlannerGridView(state, action: PayloadAction<string>) {
      state.toggledView = action.payload;
    },
    setLocalFilterValue(state, action: PayloadAction<IDynamicFormValue>) {
      state.localFilterValue = action.payload;
    },
    toggleDrawerView(
      state,
      action: PayloadAction<{
        view: DRAWER_VIEW_TYPE;
        productId: number;
        productKeyRef: string;
      }>
    ) {
      state.drawerViewMetadata.isDrawerOpen =
        !state.drawerViewMetadata.isDrawerOpen;
      const { productId, view, productKeyRef = '' } = action.payload;
      state.drawerViewMetadata.viewType = view;
      state.drawerViewMetadata.productId = productId;
      state.drawerViewMetadata.productKeyRef = productKeyRef;
    },
    addCommentForCommentDrawerParentItem(
      state,
      action: PayloadAction<{
        comment: IProductComment;
        productIndex: number;
        key: string;
      }>
    ) {
      const { comment, productIndex, key } = action.payload;
      const product = state.productsWithComments.find(
        (data) => data.parent.data[key] === productIndex
      );
      product.parent.data.comment_details.unshift(comment);
      state.isSummaryRefreshRequired = true;
    },
    addCommentForCommentDrawerChildItem(
      state,
      action: PayloadAction<{
        comment: IProductComment;
        childId: number;
        parentId: number;
      }>
    ) {
      const { comment, childId, parentId } = action.payload;
      const parentCommentsData = state.productsWithComments.find(
        (data) => data.parent.data[data.parent.data.key] === parentId
      );
      const updateChildComment = (
        childComments: IBulkCommentsDrawerResponse[]
      ) => {
        // eslint-disable-next-line array-callback-return
        childComments.forEach((child) => {
          if (child.parent.data[child.parent.data.key] === childId) {
            child.parent.data.comment_details.unshift(comment);
          }
          if (child.children && child.children.length) {
            updateChildComment(child.children);
          }
        });
      };
      updateChildComment(parentCommentsData.children);
      state.isSummaryRefreshRequired = true;
    },
    updateCommentForCommentDrawerParentItem(
      state,
      action: PayloadAction<{
        comment: IProductComment;
        productIndex: number;
        key: string;
      }>
    ) {
      const { comment, productIndex, key } = action.payload;
      const product = state.productsWithComments.find(
        (data) => data.parent.data[key] === productIndex
      ).parent.data;
      const commentIndex = product.comment_details.findIndex(
        (childComment) => childComment.pk_id === comment.pk_id
      );
      if (commentIndex > -1) {
        product.comment_details[commentIndex] = comment;
      }
      state.isSummaryRefreshRequired = true;
    },
    updateCommentForCommentDrawerChildItem(
      state,
      action: PayloadAction<{
        comment: IProductComment;
        childId: number;
        parentId: number;
      }>
    ) {
      const { comment, childId, parentId } = action.payload;
      const parentCommentsData = state.productsWithComments.find(
        (data) => data.parent.data[data.parent.data.key] === parentId
      );
      const updateChildComment = (
        childComments: IBulkCommentsDrawerResponse[]
      ) => {
        // eslint-disable-next-line array-callback-return
        childComments.forEach((child) => {
          if (child.parent.data[child.parent.data.key] === childId) {
            const commentIndex = child.parent.data.comment_details.findIndex(
              (childComment) => childComment.pk_id === comment.pk_id
            );
            if (commentIndex > -1) {
              child.parent.data.comment_details[commentIndex] = comment;
            }
          }
          if (child.children && child.children.length) {
            updateChildComment(child.children);
          }
        });
      };
      updateChildComment(parentCommentsData.children);
      state.isSummaryRefreshRequired = true;
    },
    deleteCommentForCommentDrawerParentItem(
      state,
      action: PayloadAction<{
        commentId: number;
        productIndex: number;
        key: string;
      }>
    ) {
      const { commentId, productIndex, key } = action.payload;
      const product = state.productsWithComments.find(
        (data) => data.parent.data[key] === productIndex
      ).parent.data;
      const commentIndex = product.comment_details.findIndex(
        (childComment) => childComment.pk_id === commentId
      );
      if (commentIndex > -1) {
        product.comment_details.splice(commentIndex, 1);
      }
    },
    deleteCommentForCommentDrawerChildItem(
      state,
      action: PayloadAction<{
        commentId: number;
        childId: number;
        parentId: number;
      }>
    ) {
      const { commentId, childId, parentId } = action.payload;
      const parentCommentsData = state.productsWithComments.find(
        (data) => data.parent.data[data.parent.data.key] === parentId
      );
      const updateChildComment = (
        childComments: IBulkCommentsDrawerResponse[]
      ) => {
        // eslint-disable-next-line array-callback-return
        childComments.forEach((child) => {
          if (child.parent.data[child.parent.data.key] === childId) {
            const commentIndex = child.parent.data.comment_details.findIndex(
              (childComment) => childComment.pk_id === commentId
            );
            if (commentIndex > -1) {
              child.parent.data.comment_details.splice(commentIndex, 1);
            }
          }
          if (child.children && child.children.length) {
            updateChildComment(child.children);
          }
        });
      };
      updateChildComment(parentCommentsData.children);
    },
    toggleDrawerParentItemOpportunity(
      state,
      action: PayloadAction<{
        selectedOpportunities: number[];
        changedOpportunity: IOpportunity;
        isOpportunityChecked: boolean;
        parentDrawerInfo: DrawerProductItem;
      }>
    ) {
      const {
        selectedOpportunities,
        changedOpportunity,
        isOpportunityChecked,
        parentDrawerInfo
      } = action.payload;
      // Getting the parent data to mark the opportunity from the planner state
      const currentDrawerData: DrawerProductItem =
        state.drawerViewMetadata.drawerData.drawer_data.find(
          (drawerInfo: DrawerProductItem) =>
            drawerInfo.parent.data.id === parentDrawerInfo.parent.data.id
        );
      if (isOpportunityChecked) {
        // Updating the opportunity for search planner varibale
        currentDrawerData.parent.data.selected_opportunity =
          selectedOpportunities.filter(
            (opportunityId) =>
              !changedOpportunity.disables_opportunity.includes(opportunityId)
          );
        // Following function is updating the opportunity for childrens
        const updateChildrensOpportunity = (
          drawerData: DrawerProductItem[]
        ) => {
          drawerData.forEach((child) => {
            if (
              !child.parent.data.selected_opportunity.includes(
                changedOpportunity.pk_id
              )
            ) {
              child.parent.data.selected_opportunity.push(
                changedOpportunity.pk_id
              );
            }
            /**
             * Removing any of the opportunities in child that should be removed on check of current opportunity
             */
            child.parent.data.selected_opportunity =
              child.parent.data.selected_opportunity.filter(
                (opportunityId) =>
                  !changedOpportunity.disables_opportunity.includes(
                    opportunityId
                  )
              );
            // Iterating recursively to update the children's opportunities
            if (child.children.length) {
              updateChildrensOpportunity(child.children);
            }
          });
        };
        updateChildrensOpportunity(currentDrawerData.children);
      } else {
        currentDrawerData.parent.data.selected_opportunity =
          selectedOpportunities;
        // Following function is updating the opportunity for childrens
        const removeChildrensOpportunity = (
          drawerData: DrawerProductItem[]
        ) => {
          drawerData.forEach((child) => {
            if (
              child.parent.data.selected_opportunity.includes(
                changedOpportunity.pk_id
              )
            ) {
              child.parent.data.selected_opportunity.splice(
                child.parent.data.selected_opportunity.indexOf(
                  changedOpportunity.pk_id
                ),
                1
              );
            }
            // Iterating recursively to remove the children's opportunities
            if (child.children.length) {
              removeChildrensOpportunity(child.children);
            }
          });
        };
        removeChildrensOpportunity(currentDrawerData.children);
      }
    },
    toggleDrawerChildItemOpportunity(
      state,
      action: PayloadAction<{
        selectedOpportunities: number[];
        changedOpportunity: IOpportunity;
        isOpportunityChecked: boolean;
        indexOfChild: number;
        parentDrawerDetail: DrawerProductItem;
        childrenDrawerDetail: DrawerProductItem;
      }>
    ) {
      const {
        selectedOpportunities,
        changedOpportunity,
        parentDrawerDetail,
        childrenDrawerDetail
      } = action.payload;
      // Getting the parent data to mark the opportunity from the planner state
      const currentParentDrawerData: DrawerProductItem =
        state.drawerViewMetadata.drawerData.drawer_data.find(
          (drawerInfo: DrawerProductItem) =>
            drawerInfo.parent.data.id === parentDrawerDetail.parent.data.id
        );

      const traverseInverseResults = (drawerData: DrawerProductItem[]) => {
        drawerData.forEach((childData) => {
          if (
            childrenDrawerDetail.parent.data[
            childrenDrawerDetail.parent.data.key
            ] === childData.parent.data[childrenDrawerDetail.parent.data.key] &&
            childrenDrawerDetail.parent.data[
            currentParentDrawerData.parent.data.key
            ] === childData.parent.data[currentParentDrawerData.parent.data.key]
          ) {
            childData.parent.data.selected_opportunity =
              selectedOpportunities.filter(
                (opportunityId) =>
                  !changedOpportunity.disables_opportunity.includes(
                    opportunityId
                  )
              );
          }
        });
      };
      traverseInverseResults(currentParentDrawerData.children);
      let flattenDrawerData = [];
      const flat = (headerData: DrawerProductItem[]) => {
        headerData.forEach((a: DrawerProductItem) => {
          if (a) {
            flattenDrawerData.push(a.parent.data);
          }
          if (Array.isArray(a.children) && a.children) {
            if (a.children.length) {
              //Not removing from SonarQube due to unclear business logic.
              flattenDrawerData = flattenDrawerData.concat(flat(a.children));  //NOSONAR
            }
          }
        });
      };
      flat(currentParentDrawerData.children);
      currentParentDrawerData.parent.data.selected_opportunity =
        state.drawerViewMetadata.drawerData.opportunities[
          currentParentDrawerData.parent.data.key
        ]
          .map((opportunity) => {
            const childrenWithParentOpportunityId = flattenDrawerData.filter(
              (childProduct) =>
                childProduct.selected_opportunity.includes(opportunity.pk_id)
            );
            if (!childrenWithParentOpportunityId.length) {
              return null;
            }
            if (
              childrenWithParentOpportunityId.length ===
              flattenDrawerData.length
            ) {
              return opportunity.pk_id;
            }
            return opportunity.intermediate_opp_id;
          })
          .filter((opportunityId) => opportunityId !== null);
    },
    addNewCommentForProductDrawerParentItem(
      state,
      action: PayloadAction<{
        comment: IProductComment;
        currentDrawerInfo: DrawerProductItem;
      }>
    ) {
      const { comment, currentDrawerInfo } = action.payload;
      const currentDrawerDetail =
        state.drawerViewMetadata.drawerData.drawer_data.find(
          (data) => data.parent.data.id === currentDrawerInfo.parent.data.id
        );
      currentDrawerDetail.parent.data.comment_details.unshift(comment);
      state.isSummaryRefreshRequired = true;
    },
    addNewCommentForProductDrawerChildItem(
      state,
      action: PayloadAction<{
        commentIndex: number;
        comment: IProductComment;
        currentDrawerInfo: DrawerProductItem;
        parentDrawerInfo: DrawerProductItem;
      }>
    ) {
      const { comment, currentDrawerInfo, parentDrawerInfo } = action.payload;

      const parentDrawerDetail =
        state.drawerViewMetadata.drawerData.drawer_data.find(
          (data) => data.parent.data.id === parentDrawerInfo.parent.data.id
        );

      const updateChildComment = (drawerData: DrawerProductItem[]) => {
        // eslint-disable-next-line array-callback-return
        drawerData.forEach((child) => {
          if (
            child.parent.data[child.parent.data.key] ===
            currentDrawerInfo.parent.data[currentDrawerInfo.parent.data.key]
          ) {
            child.parent.data.comment_details.unshift(comment);
          }
          if (child.children && child.children.length) {
            updateChildComment(parentDrawerDetail.children);
          }
        });
      };
      updateChildComment(parentDrawerDetail.children);
      state.isSummaryRefreshRequired = true;
    },
    updateCommentForProductDrawerParentItem(
      state,
      action: PayloadAction<{
        comment: IProductComment;
        currentDrawerInfo: DrawerProductItem;
      }>
    ) {
      const { comment, currentDrawerInfo } = action.payload;

      const parentDrawerDetail =
        state.drawerViewMetadata.drawerData.drawer_data.find(
          (data) => data.parent.data.id === currentDrawerInfo.parent.data.id
        );

      const commentIndex =
        parentDrawerDetail.parent.data.comment_details.findIndex(
          (parentComment) => parentComment.pk_id === comment.pk_id
        );
      if (commentIndex > -1) {
        parentDrawerDetail.parent.data.comment_details[commentIndex] = comment;
      }
      state.isSummaryRefreshRequired = true;
    },
    updateCommentForProductDrawerChildItem(
      state,
      action: PayloadAction<{
        itemIndex: number;
        comment: IProductComment;
        currentDrawerInfo: DrawerProductItem;
        parentDrawerInfo: DrawerProductItem;
      }>
    ) {
      const { comment, currentDrawerInfo, parentDrawerInfo } = action.payload;
      const parentDrawerDetail =
        state.drawerViewMetadata.drawerData.drawer_data.find(
          (data) => data.parent.data.id === parentDrawerInfo.parent.data.id
        );

      const updateChildComment = (drawerData: DrawerProductItem[]) => {
        // eslint-disable-next-line array-callback-return
        drawerData.forEach((child) => {
          if (child.parent.data.id === currentDrawerInfo.parent.data.id) {
            const commentIndex = child.parent.data.comment_details.findIndex(
              (childComment) => childComment.pk_id === comment.pk_id
            );
            if (commentIndex > -1) {
              child.parent.data.comment_details[commentIndex] = comment;
            }
          }
          if (child.children && child.children.length) {
            updateChildComment(parentDrawerDetail.children);
          }
        });
      };
      updateChildComment(parentDrawerDetail.children);
      state.isSummaryRefreshRequired = true;
    },
    deleteCommentFromProductDrawerParentItem(
      state,
      action: PayloadAction<{
        itemIndex: number;
        commentId: number;
        currentDrawerInfo: DrawerProductItem;
      }>
    ) {
      const { commentId, currentDrawerInfo } = action.payload;
      const parentDrawerDetail =
        state.drawerViewMetadata.drawerData.drawer_data.find(
          (data) => data.parent.data.id === currentDrawerInfo.parent.data.id
        );

      const commentIndex =
        parentDrawerDetail.parent.data.comment_details.findIndex(
          (parentComment) => parentComment.pk_id === commentId
        );
      if (commentIndex > -1) {
        parentDrawerDetail.parent.data.comment_details.splice(commentIndex, 1);
      }
      state.isSummaryRefreshRequired = true;
    },
    deleteCommentFromProductDrawerChildItem(
      state,
      action: PayloadAction<{
        itemIndex: number;
        commentId: number;
        currentDrawerInfo: DrawerProductItem;
        parentDrawerInfo: DrawerProductItem;
      }>
    ) {
      const { commentId, currentDrawerInfo, parentDrawerInfo } = action.payload;

      const parentDrawerDetail =
        state.drawerViewMetadata.drawerData.drawer_data.find(
          (data) => data.parent.data.id === parentDrawerInfo.parent.data.id
        );

      const updateChildComment = (drawerData: DrawerProductItem[]) => {
        // eslint-disable-next-line array-callback-return
        drawerData.forEach((child) => {
          if (child.parent.data.id === currentDrawerInfo.parent.data.id) {
            const commentIndex = child.parent.data.comment_details.findIndex(
              (childComment) => childComment.pk_id === commentId
            );
            if (commentIndex > -1) {
              child.parent.data.comment_details.splice(commentIndex, 1);
            }
          }
          if (child.children && child.children.length) {
            updateChildComment(parentDrawerDetail.children);
          }
        });
      };
      updateChildComment(parentDrawerDetail.children);
      state.isSummaryRefreshRequired = true;
    },
    // handler to reset the product mapping 
    resetEanZrepDetails(state) {
      state.eanZrepTableMetadata.productMapping = {};
    },
    resetGridMetrics(state) {
      state.localFilterInitialValues =
        state.localFilterFormFields.reduce<IDynamicFormValue>(
          (prev, curr) => ({ ...prev, [curr.name]: curr.default_value }),
          {}
        );
    },
    //  handler for enabling/disabling create sku form fields as per toggle value
    toggleDisableStateForSkuFormFields(
      state,
      action: PayloadAction<{
        shouldEnableAutoFillFields: boolean;
      }>
    ) {
      const { shouldEnableAutoFillFields } = action.payload;
      state.formFields
        .filter((e) => e.is_auto_fill)
        .forEach((ele) => {
          ele.is_active = shouldEnableAutoFillFields;
        });
    },

    setSkuFormFieldOptions(
      state,
      action: PayloadAction<
        ReturnType<typeof getNextValues>['fieldNameToOptionsMap']
      >
    ) {
      const fieldNameToOptionsMap = action.payload;
      state.formFields = state.formFields.map((filter) => {
        if (fieldNameToOptionsMap[filter.name]) {
          return { ...filter, values: fieldNameToOptionsMap[filter.name] };
        }
        return filter;
      });
    },
    updateFilterFieldOptions(
      state,
      action: PayloadAction<{
        [filterName: string]: ISelectOption[];
      }>
    ) {
      const remainingFilterValues = action.payload;
      state.topCogsFiltersValue.filters = state.topCogsFiltersValue.filters.map(
        (filter) => {
          if (remainingFilterValues[filter.name]) {
            return { ...filter, values: remainingFilterValues[filter.name] };
          }
          return filter;
        }
      );
    },
    clearCogsInputs(state) {
      state.isClearClicked = true;
      state.cogsTableData = state.baseCogsTableData;
    },
    // reducer for resetting cogs filters
    resetCogsFilters(state) {
      state.topCogsFiltersValue = {
        filters: [],
        combinations: [],
        initialValues: {}
      };

      state.topCogsFiltersDefaultValue = {
        filters: [],
        combinations: [],
        initialValues: {}
      };
    },
    // reducer for toggling view/hide ph recommendation
    toggleViewPhRecommendation(state, action) {
      state.isViewPhRecommendation = action.payload;
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getGridData.pending, (state) => {
        state.drawerViewMetadata = {
          isDrawerOpen: false,
          viewType: null,
          productId: null,
          isLoading: false,
          drawerData: {
            opportunities: {},
            title: {
              parent_title: '',
              child_title: ''
            },
            level_mapping: {},
            drawer_data: [
              {
                parent: {
                  data: {
                    selected_opportunity: [],
                    imgUrl: '',
                    detail_metrics: [],
                    key: '',
                    metrics: [],
                    comment_details: [],
                    id: -1,
                    title_key: '',
                    price: null
                  }
                },
                children: []
              }
            ]
          },
          opportunitySelectionMap: [],
          initialChildOpportunities: [],
          initialParentOpportunities: [],
          isSubmittingDrawerData: false,
          productKeyRef: ''
        };
        state.fetchingGridData = true;
      })
      .addCase(getGridData.fulfilled, (state, action) => {
        state.fetchingGridData = false;
        const { gridCellMetadata, gridMatrix } = action.payload;
        state.gridLayout = gridMatrix;
        state.gridCellMetadata = gridCellMetadata;
      })
      .addCase(getGridData.rejected, (state) => {
        state.fetchingGridData = false;
      })
      .addCase(getLocalFiltersData.pending, (state) => {
        state.fetchingLocalFiltersData = true;
      })
      .addCase(getLocalFiltersData.fulfilled, (state, action) => {
        state.fetchingLocalFiltersData = false;
        const { fields, values } = action.payload;
        state.appliedFilters = action.meta.arg.filters;
        state.localFilterFormFields = fields;
        state.localFilterInitialValues = values;
        state.localFilterValue = values;
      })
      .addCase(getLocalFiltersData.rejected, (state) => {
        state.fetchingLocalFiltersData = false;
      })
      .addCase(getFilterFieldsForCogs.pending, (state) => {
        state.fetchingCogsFiltersData = true;
      })
      .addCase(getFilterFieldsForCogs.fulfilled, (state, action) => {
        state.fetchingCogsFiltersData = false;
        const filters: IMultiSelectControlField2[] = toFormFields2(
          action.payload.filters
            .slice()
            .sort(
              (filterA, filterB) =>
                filterA.placement_order - filterB.placement_order
            )
        );
        const { combinations } = action.payload;
        const filterToValuesMap = filters.reduce<{
          [key: string]: ISelectOption[];
        }>(
          (previousValue, currentValue) => ({
            ...previousValue,
            ...{ [currentValue.name]: currentValue.values }
          }),
          {}
        );
        state.topCogsFiltersValue.initialValues = filterToValuesMap;
        state.topCogsFiltersValue.combinations = combinations;
        // setting filters value
        const filtersData = filters.map((filter: IMultiSelectControlField2) => {
          const filterName = filter.name;
          if (filterToValuesMap[filterName]) {
            // eslint-disable-next-line no-param-reassign
            filter.values = filterToValuesMap[filterName].slice();
          }
          return filter;
        });
        state.topCogsFiltersValue.filters = filtersData;

        state.topCogsFiltersDefaultValue.initialValues = filterToValuesMap;
        state.topCogsFiltersDefaultValue.combinations = combinations;
        state.topCogsFiltersDefaultValue.filters = filtersData;
      })
      .addCase(getFilterFieldsForCogs.rejected, (state) => {
        state.fetchingCogsFiltersData = false;
      })
      .addCase(getTableDataForCogs.pending, (state) => {
        state.fetchingCogsTableData = true;
        state.isSimulationFailed = false;
      })
      .addCase(getTableDataForCogs.fulfilled, (state, action) => {
        state.fetchingCogsTableData = false;
        state.isSimulationFailed = false;
        state.cogsTableData = {
          ...action.payload.response
        };
        state.baseCogsTableData = {
          ...action.payload.response
        };
        state.isClearClicked = false;
        state.isSKUEditedClick = false;
        if (action.payload.isSimulation) {
          state.enableSimulation = true;
          state.isSimulating = false;
        }
      })
      .addCase(getTableDataForCogs.rejected, (state) => {
        state.fetchingCogsTableData = false;
        state.isSimulating = false;
        state.isSimulationFailed = true;
      })

      .addCase(resetCogsSimulationData.pending, (state) => {
        state.fetchingCogsTableData = true;
        state.isSimulationFailed = false;
      })
      .addCase(resetCogsSimulationData.fulfilled, (state, action) => {
        state.fetchingCogsTableData = false;
        state.isSimulationFailed = false;
        state.cogsTableData = {
          ...action.payload.response
        };
        state.baseCogsTableData = {
          ...action.payload.response
        };
        state.isClearClicked = false;
        state.isSKUEditedClick = false;
      })
      .addCase(resetCogsSimulationData.rejected, (state) => {
        state.fetchingCogsTableData = false;
        state.isSimulating = false;
        state.isSimulationFailed = true;
      })

      .addCase(submitOpportunitiesForProduct.pending, (state) => {
        state.drawerViewMetadata.isSubmittingDrawerData = true;
      })
      .addCase(submitOpportunitiesForProduct.rejected, (state) => {
        state.drawerViewMetadata.isSubmittingDrawerData = false;
      })
      .addCase(submitOpportunitiesForProduct.fulfilled, (state) => {
        state.drawerViewMetadata.isSubmittingDrawerData = false;
      })
      .addCase(submitOpportunitiesForProductsInBulk.pending, (state) => {
        state.bulkInputsMetadata.isApplyingOpportunities = true;
      })
      .addCase(submitOpportunitiesForProductsInBulk.rejected, (state) => {
        state.bulkInputsMetadata.isApplyingOpportunities = false;
      })
      .addCase(submitOpportunitiesForProductsInBulk.fulfilled, (state) => {
        state.bulkInputsMetadata.isApplyingOpportunities = false;
      })
      .addCase(resetAllOpportunities.pending, (state) => {
        state.bulkInputsMetadata.isApplyingOpportunities = true;
      })
      .addCase(resetAllOpportunities.rejected, (state) => {
        state.bulkInputsMetadata.isApplyingOpportunities = false;
      })
      .addCase(resetAllOpportunities.fulfilled, (state, action) => {
        state.bulkInputsMetadata.isApplyingOpportunities = false;

        state.bulkInputsMetadata.rows.forEach((row) => {
          row.opportunities = [];
          row.subRows.forEach((subRow) => {
            subRow.opportunities = [];
          });
        });
        state.gridLayout.grid_data.forEach((row) => {
          row.forEach((cell) => {
            cell.ppg_toggle.forEach((ppg) => {
              ppg.parent.data.opportunities = [];
            });
            cell.sku_toggle.forEach((ppgGroupedBySKU) => {
              ppgGroupedBySKU.parent.data.opportunities = [];
              ppgGroupedBySKU.children.forEach((sku) => {
                sku.parent.data.opportunities = [];
              });
            });
          });
        });

        state.isSummaryRefreshRequired = true;
      })
      .addCase(resetDrawerDetails.pending, (state) => {
        state.drawerViewMetadata.isSubmittingDrawerData = true;
      })
      .addCase(resetDrawerDetails.rejected, (state) => {
        state.drawerViewMetadata.isSubmittingDrawerData = false;
      })
      .addCase(resetDrawerDetails.fulfilled, (state, action) => {
        state.drawerViewMetadata.isSubmittingDrawerData = false;
        const resetAll = action.payload;
        state.drawerViewMetadata.isSubmittingDrawerData = false;
        // Following function is resetting the opportunity/comments for childrens/parent
        const updateChildrensOpportunity = (
          drawerData: DrawerProductItem[]
        ) => {
          drawerData.forEach((child) => {
            if (resetAll) {
              child.parent.data.comment_details = [];
            }
            child.parent.data.selected_opportunity = [];
            // Iterating recursively to reset the children's opportunities
            if (child.children.length) {
              updateChildrensOpportunity(child.children);
            }
          });
        };
        updateChildrensOpportunity(
          state.drawerViewMetadata.drawerData.drawer_data
        );
        state.isSummaryRefreshRequired = true;
      })
      .addCase(getOpportunitySummaryTableDataV2.pending, (state) => {
        state.bulkInputsMetadata.isApplyingOpportunities = true;
        state.isFetchingOpportunitySummaryData = true;
      })
      .addCase(getOpportunitySummaryTableDataV2.rejected, (state) => {
        state.bulkInputsMetadata.isApplyingOpportunities = false;
        state.isFetchingOpportunitySummaryData = false;
      })
      .addCase(getOpportunitySummaryTableDataV2.fulfilled, (state, action) => {
        state.bulkInputsMetadata.isApplyingOpportunities = false;
        state.appliedFilters = action.meta.arg.filters;
        state.isSummaryRefreshRequired = false;
        state.summaryOpportunitiesTableData = action.payload;
        state.isFetchingOpportunitySummaryData = false;
      })
      .addCase(getBulkInputsTableOpportunity.pending, (state) => {
        state.bulkInputsMetadata.isApplyingOpportunities = true;
      })
      .addCase(getBulkInputsTableOpportunity.rejected, (state) => {
        state.bulkInputsMetadata.isApplyingOpportunities = false;
      })
      .addCase(getBulkInputsTableOpportunity.fulfilled, (state, action) => {
        state.bulkInputsMetadata.isApplyingOpportunities = false;
        state.bulkInputsTableOpportunities = action.payload;
      })
      .addCase(addBulkCommentsByOpportunity.pending, (state) => {
        state.bulkInputsMetadata.isAddingComment = true;
      })
      .addCase(addBulkCommentsByOpportunity.rejected, (state) => {
        state.bulkInputsMetadata.isAddingComment = false;
      })
      .addCase(addBulkCommentsByOpportunity.fulfilled, (state) => {
        state.bulkInputsMetadata.isAddingComment = false;
        state.isSummaryRefreshRequired = true;
      })
      .addCase(getProductsWithComments.pending, (state) => {
        state.drawerViewMetadata.isLoading = true;
      })
      .addCase(getProductsWithComments.rejected, (state) => {
        state.drawerViewMetadata.isLoading = false;
      })
      .addCase(getProductsWithComments.fulfilled, (state, action) => {
        state.drawerViewMetadata.isLoading = false;
        state.productsWithComments = action.payload;
      })
      .addCase(getOpportunitySummaryTableData.pending, (state) => {
        state.isFetchingOpportunitySummaryData = true;
      })
      .addCase(getOpportunitySummaryTableData.rejected, (state) => {
        state.isFetchingOpportunitySummaryData = false;
      })
      .addCase(getOpportunitySummaryTableData.fulfilled, (state, action) => {
        state.isFetchingOpportunitySummaryData = false;
        state.isSummaryRefreshRequired = false;
        state.opportunitySummaryDetails = action.payload;
      })

      .addCase(getPHRecommendations.pending, (state) => {
        // setting grid data and opp summary table data fetching state to true
        state.fetchingGridData = true;
        state.isFetchingOpportunitySummaryData = true;
        state.isFetchingPhRecommendations = true;
      })
      .addCase(getPHRecommendations.rejected, (state) => {
        state.isFetchingPhRecommendations = false;
      })
      .addCase(getPHRecommendations.fulfilled, (state, action) => {
        state.isFetchingPhRecommendations = false;
      })
      .addCase(addNewComment.fulfilled, (state) => {
        state.isSummaryRefreshRequired = true;
      })
      .addCase(updateComment.fulfilled, (state) => {
        state.isSummaryRefreshRequired = true;
      })
      .addCase(deleteCommentById.fulfilled, (state) => {
        state.isSummaryRefreshRequired = true;
      })
      .addCase(getAddSKUFormFields.pending, (state) => {
        state.isFetchingFormFields = true;
      })
      .addCase(getAddSKUFormFields.rejected, (state) => {
        state.isFetchingFormFields = false;
      })
      .addCase(getAddSKUFormFields.fulfilled, (state, action) => {
        state.isFetchingFormFields = false;
        const { combinations, fields } = action.payload;
        state.formFields = fields;
        state.addSKUFieldCombinations = combinations;
      })
      .addCase(getBulkInputsTableRows.pending, (state) => {
        state.isFetchingBulkInputs = true;
        state.bulkInputsMetadata.rows = [];
        state.bulkInputsMetadata.originalRows = [];
      })
      .addCase(getBulkInputsTableRows.rejected, (state) => {
        state.isFetchingBulkInputs = false;
        state.bulkInputsMetadata.rows = [];
        state.bulkInputsMetadata.originalRows = [];
      })
      .addCase(getBulkInputsTableRows.fulfilled, (state, action) => {
        state.isFetchingBulkInputs = false;
        state.bulkInputsMetadata.rows = action.payload;
        state.bulkInputsMetadata.originalRows = action.payload;
      })
      .addCase(getPlannerToggles.pending, (state) => {
        state.isFetchingToggleData = true;
      })
      .addCase(getPlannerToggles.fulfilled, (state, action) => {
        state.isFetchingToggleData = false;
        state.toggles = action.payload;
        state.toggledView = state.toggles[0].key;
      })
      .addCase(getPlannerToggles.rejected, (state) => {
        state.isFetchingToggleData = false;
      })
      .addCase(getDynamicDrawerData.pending, (state) => {
        state.drawerViewMetadata.isLoading = true;
      })
      .addCase(getDynamicDrawerData.rejected, (state) => {
        state.drawerViewMetadata.isLoading = false;
      })
      .addCase(getDynamicDrawerData.fulfilled, (state, action) => {
        state.drawerViewMetadata.isLoading = false;
        state.drawerViewMetadata.drawerData = action.payload;
      })
      .addCase(getEanZrepTableData.pending, (state) => {
        state.eanZrepTableMetadata.isLoading = true;
      })
      .addCase(getEanZrepTableData.rejected, (state) => {
        state.eanZrepTableMetadata.isLoading = false;
      })
      .addCase(getEanZrepTableData.fulfilled, (state, action) => {
        state.eanZrepTableMetadata.isLoading = false;
        state.eanZrepTableMetadata.productMapping[action.payload.productName] = action.payload.data;
      })
});
/* eslint-enable no-param-reassign */

export const {
  setLocalFilterValue,
  togglePlannerGridView,
  toggleDrawerView,
  resetEanZrepDetails,
  resetGridMetrics,
  toggleDrawerChildItemOpportunity,
  toggleDrawerParentItemOpportunity,
  setCogsDialogVisible,
  resetCogsSimulation,
  handleCogsSimulationFlagValue,
  handleCogsFailedFlagValue,
  clearCogsInputs,
  resetCogsFilters,
  resetBulkInputsTableRows,
  setOpportunities,
  setAllOpportunities,
  updateFilterFieldOptions,
  addNewCommentForProductDrawerParentItem,
  addNewCommentForProductDrawerChildItem,
  deleteCommentFromProductDrawerChildItem,
  deleteCommentFromProductDrawerParentItem,
  updateCommentForProductDrawerChildItem,
  updateCommentForProductDrawerParentItem,
  addCommentForCommentDrawerChildItem,
  addCommentForCommentDrawerParentItem,
  deleteCommentForCommentDrawerChildItem,
  deleteCommentForCommentDrawerParentItem,
  updateCommentForCommentDrawerChildItem,
  updateCommentForCommentDrawerParentItem,
  setSkuFormFieldOptions,
  toggleDisableStateForSkuFormFields,
  toggleViewPhRecommendation
} = plannerSliceV2.actions;

export default plannerSliceV2;
